import { DefaultRootState } from '@databases/redux';
import {
  Database,
  DatabaseFieldType,
  DatabaseValue,
  ImportedFileType,
} from '@databases/types/database';
import {
  convertType,
  getInitialValue,
  isEmptyValue,
  parseFromHtml,
} from '@databases/utils/database/field-types';
import { getTextContent, replaceBlankCharacters } from '@databases/utils/lib/dom';
import { Node } from 'domhandler';

import { parse } from '../shims/papaparse';
import { ImportResult, ImportSettings, ParsedImportFile } from '../types';
import { resolveEntryReference, resolveFieldReference } from '../util';

/**
 * Takes an imported file generated by parseImportFile() and parses it according to the specified import settings.
 */
export function applyImportSettings(
  database: Database,
  file: ParsedImportFile,
  settings: ImportSettings,
  state: DefaultRootState
):
  | { success: true; result: ImportResult }
  | {
      success: false;
      error: { tag: 'UNKNOWN_FILE_TYPE'; fileType: string };
    } {
  switch (file.type) {
    case ImportedFileType.HTML: {
      const cols = file.content.reduce((res, row) => Math.max(row.length, res), 0);
      const colsArr = [...new Array(cols)];
      const rows: ImportResult['rows'] = file.content.map((row, rowIdx) => {
        const entry = resolveEntryReference(database, settings.entryReferences[rowIdx]);
        return {
          isSelected: settings.selectedRows[rowIdx] ?? true,
          cols: colsArr.map((x, i) => {
            const cell = row[i];
            const field = resolveFieldReference(database, settings.fieldReferences[i]);
            const value = !field
              ? undefined
              : cell
              ? parseFromHtml(cell, { database, field, entry: entry! }, state)
              : getInitialValue(field.type);
            return {
              text: cell
                ? getTextContent(cell, (node: Node) =>
                    replaceBlankCharacters(node, false)
                  )
                : '',
              value,
              isEmpty: !value || isEmptyValue({ database, field: field!, value }, state),
              fieldId: field?.id,
              entryId: entry?.id,
            };
          }),
        };
      });
      return { success: true, result: { colCount: cols, rows } };
    }

    case ImportedFileType.CSV: {
      const data = parse<string[]>(file.content, { skipEmptyLines: true });
      const cols = data.data.reduce((res, row) => Math.max(row.length, res), 0);
      const colsArr = [...new Array(cols)];
      const rows: ImportResult['rows'] = data.data.map((row, rowIdx) => {
        const entry = resolveEntryReference(database, settings.entryReferences[rowIdx]);
        return {
          isSelected: settings.selectedRows[rowIdx] ?? true,
          cols: colsArr.map((x, i) => {
            const text = row[i] ?? '';
            const value: DatabaseValue = { type: DatabaseFieldType.TEXT, value: text };
            const field = resolveFieldReference(database, settings.fieldReferences[i]);
            const convertedValue = field
              ? convertType({ database, field, value }, field.type, state)
              : value;
            return {
              text,
              value,
              isEmpty: field
                ? isEmptyValue({ database, field, value: convertedValue }, state)
                : !text,
              fieldId: field?.id,
              entryId: entry?.id,
            };
          }),
        };
      });
      return { success: true, result: { colCount: cols, rows } };
    }

    default:
      return {
        success: false,
        error: { tag: 'UNKNOWN_FILE_TYPE', fileType: (file as any).type },
      };
  }
}
